import { Property } from "../types/property-types";
import { BreadcrumbArgs, Link } from "../types/navigation-types";
import { urlFormat } from "../seo/seo-helper";
import { getSiteBaseUrl } from "../helpers/site-helper";
import { getPdpUrlById } from "../helpers";

/**
 * Builds a breadcrumb navigation array based on the provided property and arguments.
 *
 * @param {Property} property - The property object containing details about the property.
 * @param {BreadcrumbArgs} args - The arguments for building the breadcrumb, including options like home link, segment tokens, etc.
 * @returns {Link[]} An array of Link objects representing the breadcrumb navigation.
 */
export const buildBreadcrumb = async (
  property: Property,
  args: BreadcrumbArgs
): Promise<Link[]> => {
  const links: Link[] = [];

  // Include home link if specified in the arguments
  if (args.includeHome) {
    links.push({
      href: args.homeHost,
      text: args.homeText,
    });
  }

  // Iterate over segment tokens to build the breadcrumb links
  for (const token of args.segmentTokens) {
    switch (token) {
      case "{propertyName}": {
        await addBreadcrumbProperty(property, links);
        break;
      }
      case "{city}": {
        const urlSlug = buildPdpCityUrl(property);
        if (urlSlug && property.location?.city) {
          await addBreadcrumb(
            {
              urlSlug,
              linkText: property.location.city,
            },
            args.searchHost,
            links
          );
        }
        break;
      }
      case "{locationGuide}": {
        if (args.neighborhoodLink && args.neighborhoodText) {
          links.push({
            href: args.neighborhoodLink,
            text: args.neighborhoodText,
          })
        }
      }
    }
  }
  return links;
};

/**
 * Adds a breadcrumb place to the links array.
 *
 * @param {object} link - The link object containing the URL slug and link text.
 * @param {string} link.urlSlug - The URL slug for the place.
 * @param {string} link.linkText - The display text for the link.
 * @param {string} searchHost - The base URL for the search host.
 * @param {Link[]} links - The array of Link objects to which the new link will be added.
 */
export const addBreadcrumb = async (
  link: { urlSlug: string; linkText?: string },
  searchHost: string,
  links: Link[]
) => {
  if (link.linkText) {
    links.push({
      href: `${searchHost.endsWith("/") ? searchHost : searchHost + "/"}${link.urlSlug.startsWith("/") ? link.urlSlug.slice(1) : link.urlSlug}`,
      text: link.linkText,
    });
  }
};

/**
 * Adds a breadcrumb property to the links array.
 *
 * @param {LocationAddress | undefined} location - The location object containing details about the location.
 * @param {string} propertyName - The name of the property.
 * @param {string} pdpHost - The base URL for the PDP host.
 * @param {Link[]} links - The array of Link objects to which the new link will be added.
 */
export const addBreadcrumbProperty = async (
  property: Property,
  links: Link[],
) => {
  const link: Link = {
    href: getPdpUrlById(
      property.id,
      property.name,
      property.location?.city,
      property.location?.state,
    ),
    text: property.name,
  };

  links.push(link);
};

/**
 * Generates a location guide link based on the provided link.
 *
 * This function extracts the relevant path from the given link by identifying the segment
 * corresponding to the country code and reconstructing the path from that point. It then
 * appends this path to the editorial site base path to form the
 * full location guide link.
 *
 * @param {string} link - The full URL or path from which the location guide link will be derived.
 * @returns {string | null} The constructed location guide link, or `null` if an error occurs during processing.
 */
export const getLocationGuideLink = (link: string): string | null => {
  try {
    const editorialBase = getSiteBaseUrl("Editorial");
    // Check if link is absolute (starts with http:// or https://)
    const absoluteUrlPattern = /^https?:\/\//i;
    if (absoluteUrlPattern.test(link)) {
      // Parse the URL and replace the origin with editorialBase
      const url = new URL(link);
      // Remove leading slash from pathname if editorialBase already ends with a slash
      const path = url.pathname.startsWith("/") && editorialBase.endsWith("/")
        ? url.pathname.slice(1)
        : url.pathname;
      // Remove any double slashes
      return `${editorialBase}${path}${url.search}${url.hash}`.replace(/([^:]\/)\/+/, "$1");
    } else {
      // Relative URL: just append to editorialBase
      // Remove leading slash from link if editorialBase already ends with a slash
      const relativePath = link.startsWith("/") && editorialBase.endsWith("/")
        ? link.slice(1)
        : link;
      return `${editorialBase}${relativePath}`;
    }
  } catch {
    return null;
  }
};

export const buildPdpCityUrl = (property: Property): string | null => {
  if (!property.location?.city || !property.location?.state) {
    return null;
  }

  return `/s/${urlFormat(property.location.city, false)}-${urlFormat(property.location.state, false)}`;
};

export const buildPdpMarketAreaUrl = (property: Property): string | null => {
  if (
    !property.location?.city ||
    !property.location?.state ||
    !property.location?.greystarMarketArea
  ) {
    return null;
  }

  if (
    property.location.greystarMarketArea.toLowerCase().trim() ===
    property.location.city.toLowerCase().trim()
  ) {
    return null;
  }

  return `/s/${urlFormat(property.location.city, false)}-${urlFormat(property.location.greystarMarketArea, false)}-${urlFormat(property.location.state, false)}`;
};

export const buildPdpNeighborhoodUrl = (property: Property): string | null => {
  if (
    !property.location?.city ||
    !property.location?.state ||
    !property.location?.greystarNeighborhood
  ) {
    return null;
  }

  if (
    property.location.greystarNeighborhood.toLowerCase().trim() ===
    property.location.city.toLowerCase().trim()
  ) {
    return null;
  }

  return `/s/${urlFormat(property.location.city, false)}-${urlFormat(property.location.greystarNeighborhood, false)}-${urlFormat(property.location.state, false)}`;
};
